import React, { useCallback, useEffect } from "react";
import PropTypes from "prop-types";
import { FaChevronDown, FaChevronUp, FaExternalLinkAlt } from "react-icons/fa";
import { useIncidentDetails } from "./hooks/useIncidentDetails";
import { useIncidentExpansionTracking } from "./hooks/useIncidentExpansionTracking";
import ExpandedContent from "./components/ExpandedContent";
import IncidentErrorBoundary from "./components/IncidentErrorBoundary";
import { trackViewFullDetails, trackMissingResolveDate } from "../../../utils/modernUITracking";
import { formatTimestamp } from "../Status/statusUtils";
import ExecSummaryLink from "./components/ExecSummaryLink";
import styles from "./IncidentCard.module.css";

/**
 * IncidentCard - Display individual incident with beautiful card design
 * Features: 4px blue left border, priority badge, hover effects, click navigation
 */
const IncidentCard = React.memo(
  ({
    number,
    priority = "EP4",
    shortDescription = "No description available",
    status = "OPEN",
    slackChannel = null,
    incidentStart = null,
    incidentEnd = null,
    affectedBrand = null,
    execSummaryUrl = null,
    isExpanded = false,
    onToggleExpand = null,
    enablePolling = true,
    section = 'active_incidents',
    newrelic = null,
    user = null,
  }) => {
    const { data, loading, refreshing, lastUpdated, error, refresh } =
      useIncidentDetails(number, isExpanded, enablePolling);

    useIncidentExpansionTracking({ number, isExpanded, source: 'card', section, newrelic });

    const handleToggleExpand = useCallback(
      (e) => {
        e.stopPropagation(); // Prevent card click
        if (onToggleExpand) {
          onToggleExpand();
        }
      },
      [onToggleExpand],
    );

    // Priority color mapping - Use CSS custom properties for dark mode support
    const getPriorityColor = (priority) => {
      const priorityMap = {
        EP1: "tw-bg-[var(--color-priority-ep1)]",
        EP2: "tw-bg-[var(--color-priority-ep2)]",
        EP3: "tw-bg-[var(--color-priority-ep3)]",
        EP4: "tw-bg-[var(--color-priority-ep4)]",
      };
      return priorityMap[priority.toUpperCase()] || "tw-bg-gray-500";
    };

    // Status text mapping
    const getStatusText = (status) => {
      const statusMap = {
        OPEN: "Active",
        INVESTIGATING: "Investigating",
        MONITORING: "Monitoring",
        RESOLVED: "Resolved",
        CLOSED: "Closed",
      };
      return statusMap[status.toUpperCase()] || status;
    };

    // Format timestamp
    // US2072496: delegates to the shared statusUtils.formatTimestamp instead of
    // re-implementing the same relative-then-absolute ladder locally. Behaviour is
    // unchanged apart from sub-minute values, which now read "Just now" rather
    // than "0m ago", and numeric second-precision timestamps, which are now
    // normalised to milliseconds.
    // No falsy guard needed: formatTimestamp already returns '' for null/undefined/0,
    // and both call sites below only render once the timestamp is known truthy.
    const formatTime = (timestamp) => formatTimestamp(timestamp, { relative: true });

    // US2072496: a past-incidents card with no usable resolve time renders
    // "Resolved —". Emit that data defect once per affected incident rather than
    // per render, so the state is discoverable in NewRelic. Expected volume is zero.
    //
    // Keyed off the formatted result rather than `!incidentEnd` so a truthy but
    // unparseable timestamp — which formatTime turns into '' — lands in the same
    // branch instead of rendering a bare "Resolved " and emitting nothing.
    const formattedResolveTime =
      section === 'past_incidents' ? formatTime(incidentEnd) : '';
    const missingResolveDate = section === 'past_incidents' && !formattedResolveTime;
    useEffect(() => {
      if (missingResolveDate) {
        trackMissingResolveDate(number, { surface: 'card', section }, newrelic);
      }
    }, [missingResolveDate, number, section, newrelic]);

    // Get severity based on priority
    const getSeverity = (incident) => {
      // Map priority to severity levels
      const priorityUpper = (incident.priority || priority).toUpperCase();
      if (priorityUpper === "EP1" || priorityUpper === "EP2") return "high";
      if (priorityUpper === "EP3") return "medium";
      return "low";
    };

    // Calculate incident age
    const getIncidentAge = (createdAt) => {
      if (!createdAt) return "unknown";
      const now = new Date();
      const created = new Date(createdAt);
      const diffMs = now - created;
      const diffHours = Math.floor(diffMs / (1000 * 60 * 60));
      const diffDays = Math.floor(diffHours / 24);

      if (diffDays > 0) return `${diffDays} day${diffDays > 1 ? "s" : ""}`;
      return `${diffHours} hour${diffHours !== 1 ? "s" : ""}`;
    };


    const handleCardClick = () => {
      if (onToggleExpand) {
        onToggleExpand();
      }
    };

    return (
      <div
        className={`${styles.card} ${isExpanded ? styles.expanded : ""} tw-rounded-lg tw-shadow-sm hover:tw-shadow-md tw-transition-all tw-duration-200 tw-cursor-pointer tw-border-l-4 tw-border-cox-blue-500 tw-overflow-hidden`}
        onClick={handleCardClick}
        role="button"
        tabIndex={0}
        onKeyDown={(e) => {
          if (e.key === "Enter" || e.key === " ") {
            e.preventDefault();
            handleCardClick();
          }
        }}
        aria-expanded={isExpanded}
        aria-label={`Incident ${number}: ${shortDescription}. Click to ${isExpanded ? "collapse" : "expand"} details`}
        data-testid="incident-card"
      >
        {/* Priority Badge */}
        <div className="tw-p-4 tw-pb-3">
          <div className="tw-flex tw-items-center tw-justify-between tw-mb-3">
            <span
              className={`${getPriorityColor(priority)} tw-text-white tw-text-xs tw-font-bold tw-px-2.5 tw-py-1 tw-rounded-full tw-tracking-wide`}
              data-testid="priority-badge"
            >
              {priority}
            </span>
            <div className="tw-flex tw-items-center tw-gap-2">
              {/* View Full Details */}
              <a
                href={`/problem/view/${number}`}
                className={styles.viewLink}
                onClick={(e) => {
                  e.stopPropagation();
                  trackViewFullDetails(number, { source: 'card', section }, newrelic);
                }}
                data-testid="incident-view-details-link"
              >
                View Full Details <FaExternalLinkAlt className={styles.viewLinkIcon} />
              </a>
              {/* Expand/Collapse Toggle */}
              <button
                onClick={handleToggleExpand}
                className={styles.expandToggle}
                aria-label={isExpanded ? "Collapse details" : "Expand details"}
                aria-expanded={isExpanded}
                aria-controls={`incident-${number}-details`}
                data-testid="incident-expand-toggle"
              >
                {isExpanded ? <FaChevronUp /> : <FaChevronDown />}
              </button>
            </div>
          </div>

          {/* Description */}
          <h3
            className={`tw-text-base tw-font-semibold tw-mb-3 tw-line-clamp-2 tw-leading-snug ${styles.description}`}
          >
            {shortDescription}
          </h3>

          {/* Metadata */}
          <div className="tw-space-y-2">
            <div
              className={`tw-flex tw-items-center tw-text-sm ${styles.metadataItem}`}
            >
              <svg
                className={`tw-w-4 tw-h-4 tw-mr-2 ${styles.metadataIcon}`}
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"
                />
              </svg>
              <span className="tw-font-medium">{getStatusText(status)}</span>
            </div>

            {affectedBrand && (
              <div
                className={`tw-flex tw-items-center tw-text-sm ${styles.metadataItem}`}
              >
                <svg
                  className={`tw-w-4 tw-h-4 tw-mr-2 ${styles.metadataIcon}`}
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4"
                  />
                </svg>
                <span className="tw-truncate tw-font-medium">
                  {affectedBrand}
                </span>
              </div>
            )}

            {/* US2072496: "past" is defined by resolve time, so the start time is
                hidden here — the Resolved badge below is this card's only date. */}
            {incidentStart && section !== 'past_incidents' && (
              <div
                className={`tw-flex tw-items-center tw-text-sm ${styles.metadataItem}`}
              >
                <svg
                  className={`tw-w-4 tw-h-4 tw-mr-2 ${styles.metadataIcon}`}
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"
                  />
                </svg>
                <span>{formatTime(incidentStart)}</span>
              </div>
            )}

            {slackChannel && section !== 'past_incidents' && (
              <div
                className={`tw-flex tw-items-center tw-text-sm ${styles.metadataItem}`}
              >
                <svg
                  className={`tw-w-4 tw-h-4 tw-mr-2 ${styles.metadataIcon}`}
                  fill="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path d="M5.042 15.165a2.528 2.528 0 01-2.52 2.523A2.528 2.528 0 010 15.165a2.527 2.527 0 012.522-2.52h2.52v2.52zM6.313 15.165a2.527 2.527 0 012.521-2.52 2.527 2.527 0 012.521 2.52v6.313A2.528 2.528 0 018.834 24a2.528 2.528 0 01-2.521-2.522v-6.313zM8.834 5.042a2.528 2.528 0 01-2.521-2.52A2.528 2.528 0 018.834 0a2.528 2.528 0 012.521 2.522v2.52H8.834zM8.834 6.313a2.528 2.528 0 012.521 2.521 2.528 2.528 0 01-2.521 2.521H2.522A2.528 2.528 0 010 8.834a2.528 2.528 0 012.522-2.521h6.312zM18.956 8.834a2.528 2.528 0 012.522-2.521A2.528 2.528 0 0124 8.834a2.528 2.528 0 01-2.522 2.521h-2.522V8.834zM17.688 8.834a2.528 2.528 0 01-2.523 2.521 2.527 2.527 0 01-2.52-2.521V2.522A2.527 2.527 0 0115.165 0a2.528 2.528 0 012.523 2.522v6.312zM15.165 18.956a2.528 2.528 0 012.523 2.522A2.528 2.528 0 0115.165 24a2.527 2.527 0 01-2.52-2.522v-2.522h2.52zM15.165 17.688a2.527 2.527 0 01-2.52-2.523 2.526 2.526 0 012.52-2.52h6.313A2.527 2.527 0 0124 15.165a2.528 2.528 0 01-2.522 2.523h-6.313z" />
                </svg>
                <span className="tw-truncate">{slackChannel}</span>
              </div>
            )}
          </div>

          {/* Status Badge with Severity Indicator — hidden for past/resolved incidents */}
          {section !== 'past_incidents' && (
            <div className={styles.statusBadge}>
              <span
                className={styles.severityDot}
                data-severity={getSeverity({ priority })}
              />
              <span className={styles.statusText}>
                Open for {getIncidentAge(incidentStart)}
              </span>
            </div>
          )}
          {/* US2072496: rendered even when the resolve time is missing or
              unparseable, as "Resolved —". A resolved record with no resolve time is
              a data defect that should be visible and investigated, not hidden
              behind an absent badge. */}
          {section === 'past_incidents' && (
            <div className={styles.statusBadge}>
              <span className={styles.statusText}>
                Resolved {formattedResolveTime || '—'}
              </span>
            </div>
          )}
          {section === 'past_incidents' && execSummaryUrl && (
            <div className={styles.statusBadge}>
              <ExecSummaryLink
                url={execSummaryUrl}
                incidentId={number}
                source="card"
                newrelic={newrelic}
              />
            </div>
          )}
        </div>

        {/* Expanded Content Section */}
        {isExpanded && (
          <div
            id={`incident-${number}-details`}
            className={styles.expandedSection}
            role="region"
            aria-live="polite"
          >
            <IncidentErrorBoundary incidentNumber={number}>
              <ExpandedContent
                incidentNumber={number}
                incidentTitle={shortDescription}
                data={data}
                loading={loading}
                refreshing={refreshing}
                lastUpdated={lastUpdated}
                error={error}
                section={section}
                newrelic={newrelic}
                user={user}
                onImpactCreated={refresh}
              />
            </IncidentErrorBoundary>
          </div>
        )}

        {/* Hover indicator */}
        <div
          className={`${styles.hoverIndicator} tw-h-1 tw-bg-cox-blue-500 tw-transform tw-scale-x-0 tw-transition-transform tw-duration-200 tw-origin-left`}
        ></div>
      </div>
    );
  },
);

IncidentCard.displayName = "IncidentCard";

IncidentCard.propTypes = {
  number: PropTypes.string.isRequired,
  priority: PropTypes.string,
  shortDescription: PropTypes.string,
  status: PropTypes.string,
  slackChannel: PropTypes.string,
  incidentStart: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  incidentEnd: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  affectedBrand: PropTypes.string,
  /** Exec Summary Slide link; the icon is shown only in past_incidents and only when set. */
  execSummaryUrl: PropTypes.string,
  isExpanded: PropTypes.bool,
  onToggleExpand: PropTypes.func,
  enablePolling: PropTypes.bool,
  section: PropTypes.oneOf(['active_incidents', 'past_incidents']),
  newrelic: PropTypes.object,
  user: PropTypes.object,
};

export default IncidentCard;
